import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CommentOutlined,
  DatabaseOutlined,
  InboxOutlined,
  MessageOutlined,
  NodeIndexOutlined,
  ReloadOutlined,
  RobotOutlined,
  SearchOutlined,
  ShopOutlined,
  ShoppingCartOutlined,
  SyncOutlined,
  EditOutlined,
  LikeOutlined,
  DislikeOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Drawer,
  Empty,
  Input,
  message,
  Pagination,
  Select,
  Spin,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getVendorAgentDashboardStats,
  getVendorAgentPredictionDetail,
  getVendorAgentPredictions,
  updatePredictionRemarks,
} from "../../apis/vendor_agent_dashboard.api";
import {
  IDashboardStats,
  IPredictionDetail,
  IPredictionStatus,
  IPredictionSummary,
  IVendorAgentDashboardFilters,
} from "../../interfaces/vendor_agent_dashboard.interface";
import CartItemsTab from "./cart_items_tab";
import styles from "./index.module.scss";
import InventorySyncTab from "./inventory_sync_tab";

const { RangePicker } = DatePicker;

// ----- Status helpers -----

const STATUS_LABELS: Record<IPredictionStatus, string> = {
  applied: "Applied",
  would_apply: "Would Apply",
  skipped: "Skipped",
};

const statusChipClass = (status: IPredictionStatus) => {
  if (status === "applied") return styles.chipApplied;
  if (status === "would_apply") return styles.chipWouldApply;
  return styles.chipSkipped;
};

const cardBorderClass = (status: IPredictionStatus) => {
  if (status === "applied") return styles.cardApplied;
  if (status === "would_apply") return styles.cardWouldApply;
  return styles.cardSkipped;
};

const formatRelativeTime = (iso: string) => {
  const d = dayjs(iso);
  const now = dayjs();
  const diffMin = now.diff(d, "minute");
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = now.diff(d, "hour");
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = now.diff(d, "day");
  if (diffDay < 7) return `${diffDay}d ago`;
  return d.format("DD MMM");
};

const formatPercent = (value: number | null | undefined) => {
  if (value == null) return "—";
  return `${(value * 100).toFixed(0)}%`;
};

// ============================================================================
// PredictionCard — single card in the grid
// ============================================================================

const PredictionCard: React.FC<{
  data: IPredictionSummary;
  onClick: () => void;
}> = ({ data, onClick }) => {
  const status = data.outcome.status;
  const reasoning = data.analysis.reasoning?.trim();
  const leadTime = data.prediction.predicted_lead_time;

  return (
    <div
      className={`${styles.predictionCard} ${cardBorderClass(status)}`}
      onClick={onClick}
    >
      <div className={styles.cardHead}>
        <div className={styles.cardVendor}>
          <ShopOutlined style={{ marginRight: 6, color: "#64748b" }} />
          {data.vendor.name || `Vendor #${data.vendor.id}`}
        </div>
        <div className={styles.cardTime}>{formatRelativeTime(data.created_at)}</div>
      </div>

      {(data.cart.cart_number || data.cart.variant_name) && (
        <div className={styles.cardCart}>
          {data.cart.cart_number && (
            <span className={styles.cardCartHi}>#{data.cart.cart_number}</span>
          )}
          {data.cart.cart_number && data.cart.variant_name && " · "}
          {data.cart.variant_name}
        </div>
      )}

      <div className={styles.cardMessage}>
        {data.message.body_preview || "(empty message)"}
      </div>

      <div className={styles.cardChips}>
        <span className={`${styles.chip} ${statusChipClass(status)}`}>
          {status === "applied" && <CheckCircleOutlined />}
          {status === "would_apply" && <ThunderboltOutlined />}
          {status === "skipped" && <ClockCircleOutlined />}
          {STATUS_LABELS[status]}
        </span>
        {leadTime != null && (
          <span className={`${styles.chip} ${styles.chipPrimary}`}>
            <ClockCircleOutlined /> {leadTime} {leadTime === 1 ? "day" : "days"}
          </span>
        )}
        {data.prediction.confidence != null && (
          <span className={`${styles.chip} ${styles.chipNeutral}`}>
            {formatPercent(data.prediction.confidence)} conf
          </span>
        )}
        {data.prediction.match_method && (
          <span className={`${styles.chip} ${styles.chipNeutral}`}>
            {data.prediction.match_method.replace(/_/g, " ")}
          </span>
        )}
      </div>

      <div
        className={`${styles.cardReasoning} ${
          !reasoning ? styles.cardReasoningEmpty : ""
        }`}
      >
        {reasoning ||
          "No reasoning recorded yet — newer predictions will include this."}
      </div>
    </div>
  );
};

// ============================================================================
// StatsRow — top metric cards
// ============================================================================

const StatsRow: React.FC<{ stats: IDashboardStats | null; loading: boolean }> = ({
  stats,
  loading,
}) => {
  const cards = [
    {
      label: "Total Analyses",
      value: stats?.total_predictions ?? "—",
      hint: stats ? `${stats.unique_vendors} unique vendors` : "",
      icon: <DatabaseOutlined />,
      iconClass: styles.statIconPrimary,
    },
    {
      label: "Auto Applied",
      value: stats?.applied_in_auto ?? "—",
      hint: "Updates pushed to cart",
      icon: <CheckCircleOutlined />,
      iconClass: styles.statIconSuccess,
    },
    {
      label: "Would Apply",
      value: stats?.would_apply ?? "—",
      hint: "Shadow mode (above threshold)",
      icon: <ThunderboltOutlined />,
      iconClass: styles.statIconInfo,
    },
    {
      label: "Avg Confidence",
      value: stats ? formatPercent(stats.avg_confidence) : "—",
      hint: stats?.accuracy_rate != null
        ? `${formatPercent(stats.accuracy_rate)} accuracy verified`
        : "Accuracy not verified yet",
      icon: <RobotOutlined />,
      iconClass: styles.statIconWarning,
    },
  ];

  return (
    <div className={styles.statsGrid}>
      {cards.map((c) => (
        <div className={styles.statCard} key={c.label}>
          <div className={styles.statCardHead}>
            <div className={styles.statLabel}>{c.label}</div>
            <div className={`${styles.statIcon} ${c.iconClass}`}>{c.icon}</div>
          </div>
          <div className={styles.statValue}>{loading ? "…" : c.value}</div>
          {c.hint && <div className={styles.statHint}>{c.hint}</div>}
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// DetailDrawer — full prediction detail in side panel
// ============================================================================

const DetailDrawer: React.FC<{
  open: boolean;
  loading: boolean;
  data: IPredictionDetail | null;
  onClose: () => void;
  onRemarksUpdated?: (id: number, remarks: string) => void;
}> = ({ open, loading, data, onClose, onRemarksUpdated }) => {
  const [remarks, setRemarks] = useState("");
  const [reviewCorrect, setReviewCorrect] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  // Sync local state when drawer data changes
  useEffect(() => {
    if (data) {
      setRemarks(data.remarks || "");
      // Derive review verdict from existing remarks prefix
      const r = (data.remarks || "").toLowerCase();
      if (r.startsWith("[correct]")) setReviewCorrect(true);
      else if (r.startsWith("[incorrect]") || r.startsWith("[needs adjustment]"))
        setReviewCorrect(false);
      else setReviewCorrect(null);
    }
  }, [data]);

  const handleSaveReview = async () => {
    if (!data) return;
    setSaving(true);

    // Prefix the remarks with the verdict tag if set and not already there
    let finalRemarks = remarks.trim();
    const stripped = finalRemarks.replace(/^\[(correct|incorrect|needs adjustment)\]\s*/i, "");
    if (reviewCorrect === true) finalRemarks = `[Correct] ${stripped}`;
    else if (reviewCorrect === false) finalRemarks = `[Incorrect] ${stripped}`;
    else finalRemarks = stripped;

    const res = await updatePredictionRemarks(data.id, { remarks: finalRemarks });
    if (res?.success) {
      message.success("Review saved");
      setRemarks(finalRemarks);
      onRemarksUpdated?.(data.id, finalRemarks);
    }
    setSaving(false);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={680}
      title={
        data ? (
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
              {data.vendor.name}
            </div>
            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 400 }}>
              Prediction #{data.id} · {dayjs(data.created_at).format("DD MMM YYYY, hh:mm A")}
            </div>
          </div>
        ) : (
          "Loading…"
        )
      }
      destroyOnClose
    >
      {loading || !data ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* === Cart context === */}
          <div className={styles.drawerSection}>
            <div className={styles.drawerSectionTitle}>
              <ShoppingCartOutlined /> Cart Item
            </div>
            <div className={styles.drawerCard}>
              <div className={styles.drawerKVRow}>
                <span className={styles.drawerKVLabel}>Cart Number</span>
                <span className={styles.drawerKVValue}>
                  {data.cart.cart_number || "—"}
                </span>
              </div>
              <div className={styles.drawerKVRow}>
                <span className={styles.drawerKVLabel}>Product</span>
                <span className={styles.drawerKVValue}>
                  {data.cart.variant_name || "—"}
                </span>
              </div>
              <div className={styles.drawerKVRow}>
                <span className={styles.drawerKVLabel}>Current Lead Time</span>
                <span className={styles.drawerKVValue}>
                  {data.cart.current_lead_time != null
                    ? `${data.cart.current_lead_time} days`
                    : "Not set"}
                </span>
              </div>
              {data.cart.lead_time_updated_at && (
                <div className={styles.drawerKVRow}>
                  <span className={styles.drawerKVLabel}>Last Updated</span>
                  <span className={styles.drawerKVValue}>
                    {dayjs(data.cart.lead_time_updated_at).format("DD MMM, hh:mm A")}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* === Incoming message === */}
          <div className={styles.drawerSection}>
            <div className={styles.drawerSectionTitle}>
              <MessageOutlined /> Vendor Message
            </div>
            <div className={styles.drawerMessageBubble}>
              {data.full_detail.full_message_content ||
                data.full_detail.full_message_body ||
                data.message.body_preview ||
                "(empty)"}
            </div>
            <div className={styles.drawerMessageMeta}>
              {data.full_detail.sender_name && (
                <span>From: <strong>{data.full_detail.sender_name}</strong></span>
              )}
              {data.full_detail.sender_role && (
                <Tag color="blue" style={{ marginRight: 0 }}>
                  {data.full_detail.sender_role}
                </Tag>
              )}
              {data.full_detail.message_timestamp && (
                <span>
                  {dayjs(data.full_detail.message_timestamp).format("DD MMM, hh:mm A")}
                </span>
              )}
              {data.full_detail.quoted_chain_hops != null &&
                data.full_detail.quoted_chain_hops > 0 && (
                  <Tag color="purple" style={{ marginRight: 0 }}>
                    Reply chain: {data.full_detail.quoted_chain_hops}
                  </Tag>
                )}
            </div>
          </div>

          {/* === Gemini reasoning === */}
          <div className={styles.drawerSection}>
            <div className={styles.drawerSectionTitle}>
              <RobotOutlined /> AI Reasoning
            </div>
            {data.analysis.reasoning ? (
              <div className={styles.drawerReasoningCard}>
                "{data.analysis.reasoning}"
              </div>
            ) : (
              <div className={styles.drawerReasoningEmpty}>
                No reasoning recorded for this prediction. Newer predictions
                will include a one-line explanation from Gemini.
              </div>
            )}
          </div>

          {/* === Decision flow === */}
          <div className={styles.drawerSection}>
            <div className={styles.drawerSectionTitle}>
              <ThunderboltOutlined /> Decision Flow
            </div>
            <div className={styles.flowGrid}>
              <div className={styles.flowStep}>
                <div className={`${styles.flowStepIcon} ${styles.flowStepIconDone}`}>
                  1
                </div>
                <div className={styles.flowStepBody}>
                  <div className={styles.flowStepLabel}>Gemini Analysis</div>
                  <div className={styles.flowStepValue}>
                    Available: <strong>{String(data.analysis.is_available)}</strong>
                    {data.analysis.gemini_lead_time != null && (
                      <> · Lead time: <strong>{data.analysis.gemini_lead_time} days</strong></>
                    )}
                    {data.analysis.gemini_quantity != null && (
                      <> · Qty: <strong>{data.analysis.gemini_quantity}</strong></>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.flowStep}>
                <div className={`${styles.flowStepIcon} ${styles.flowStepIconDone}`}>
                  2
                </div>
                <div className={styles.flowStepBody}>
                  <div className={styles.flowStepLabel}>Match</div>
                  <div className={styles.flowStepValue}>
                    {data.prediction.match_method?.replace(/_/g, " ") || "—"}
                    {data.prediction.match_path && (
                      <> · {data.prediction.match_path}</>
                    )}
                    {data.prediction.confidence != null && (
                      <> · Confidence <strong>{formatPercent(data.prediction.confidence)}</strong></>
                    )}
                    {data.prediction.confidence_threshold != null && (
                      <> (threshold {formatPercent(data.prediction.confidence_threshold)})</>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.flowStep}>
                <div className={`${styles.flowStepIcon} ${styles.flowStepIconDone}`}>
                  3
                </div>
                <div className={styles.flowStepBody}>
                  <div className={styles.flowStepLabel}>Predicted</div>
                  <div className={styles.flowStepValue}>
                    Lead time:{" "}
                    <strong>
                      {data.prediction.predicted_lead_time != null
                        ? `${data.prediction.predicted_lead_time} days`
                        : "—"}
                    </strong>
                    {data.prediction.predicted_quantity != null && (
                      <> · Qty: <strong>{data.prediction.predicted_quantity}</strong></>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.flowStep}>
                <div
                  className={`${styles.flowStepIcon} ${
                    data.outcome.status === "applied"
                      ? styles.flowStepIconDone
                      : data.outcome.status === "skipped"
                      ? styles.flowStepIconSkipped
                      : ""
                  }`}
                >
                  4
                </div>
                <div className={styles.flowStepBody}>
                  <div className={styles.flowStepLabel}>Outcome</div>
                  <div className={styles.flowStepValue}>
                    {data.outcome.status === "applied" && (
                      <>
                        <CheckCircleOutlined style={{ color: "#10b981" }} />{" "}
                        <strong>Applied</strong> — cart item lead time updated
                        to <strong>{data.prediction.predicted_lead_time} days</strong>
                      </>
                    )}
                    {data.outcome.status === "would_apply" && (
                      <>
                        <ThunderboltOutlined style={{ color: "#3b82f6" }} />{" "}
                        <strong>Would have applied</strong> in auto mode
                        (shadow only)
                      </>
                    )}
                    {data.outcome.status === "skipped" && (
                      <>
                        <CloseCircleOutlined style={{ color: "#94a3b8" }} />{" "}
                        <strong>Skipped</strong> — confidence below threshold
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* === Verification === */}
          {data.full_detail.verification.match_correct != null && (
            <div className={styles.drawerSection}>
              <div className={styles.drawerSectionTitle}>
                <CheckCircleOutlined /> Verification
              </div>
              <div className={styles.drawerCard}>
                <div className={styles.drawerKVRow}>
                  <span className={styles.drawerKVLabel}>Match correct?</span>
                  <span className={styles.drawerKVValue}>
                    {data.full_detail.verification.match_correct ? (
                      <Tag color="success">Correct</Tag>
                    ) : (
                      <Tag color="error">Incorrect</Tag>
                    )}
                  </span>
                </div>
                {data.full_detail.verification.actual_lead_time != null && (
                  <div className={styles.drawerKVRow}>
                    <span className={styles.drawerKVLabel}>Actual lead time</span>
                    <span className={styles.drawerKVValue}>
                      {data.full_detail.verification.actual_lead_time} days
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* === Manual Review === */}
          <div className={styles.drawerSection}>
            <div className={styles.drawerSectionTitle}>
              <EditOutlined /> Manual Review
            </div>
            <div className={styles.reviewCard}>
              <div className={styles.reviewVerdictRow}>
                <span className={styles.reviewVerdictLabel}>Was the analysis correct?</span>
                <div className={styles.reviewVerdictBtns}>
                  <button
                    type="button"
                    className={`${styles.verdictBtn} ${styles.verdictBtnCorrect} ${
                      reviewCorrect === true ? styles.verdictBtnActive : ""
                    }`}
                    onClick={() => setReviewCorrect(reviewCorrect === true ? null : true)}
                  >
                    <LikeOutlined /> Correct
                  </button>
                  <button
                    type="button"
                    className={`${styles.verdictBtn} ${styles.verdictBtnIncorrect} ${
                      reviewCorrect === false ? styles.verdictBtnActive : ""
                    }`}
                    onClick={() => setReviewCorrect(reviewCorrect === false ? null : false)}
                  >
                    <DislikeOutlined /> Incorrect
                  </button>
                </div>
              </div>
              <Input.TextArea
                placeholder="Add remarks — e.g. 'lead time was off by 2 days', 'matched wrong item'…"
                value={remarks.replace(/^\[(correct|incorrect|needs adjustment)\]\s*/i, "")}
                onChange={(e) => setRemarks(e.target.value)}
                rows={3}
                className={styles.reviewTextarea}
              />
              <div className={styles.reviewActions}>
                <Button
                  type="primary"
                  loading={saving}
                  onClick={handleSaveReview}
                  className={styles.reviewSaveBtn}
                >
                  Save Review
                </Button>
                {data.remarks && (
                  <span className={styles.reviewSavedHint}>
                    <CheckCircleOutlined /> Previously saved
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* === Conversation context === */}
          {data.full_detail.context_messages.length > 0 && (
            <div className={styles.drawerSection}>
              <div className={styles.drawerSectionTitle}>
                <CommentOutlined /> Conversation Context
              </div>
              <div className={styles.contextList}>
                {data.full_detail.context_messages.map((m) => (
                  <div
                    key={m.id}
                    className={`${styles.contextMsg} ${
                      m.is_target ? styles.contextMsgTarget : ""
                    }`}
                  >
                    <div className={styles.contextMsgHead}>
                      <span className={styles.contextMsgRole}>
                        {m.sender_name || m.sender_role || m.role}
                      </span>
                      <span className={styles.contextMsgTime}>
                        {dayjs(m.created_at).format("DD MMM, hh:mm A")}
                      </span>
                    </div>
                    <div className={styles.contextMsgBody}>{m.content}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

// ============================================================================
// VendorAgentDashboard — main page
// ============================================================================

const VALID_TABS = ["predictions", "cart-items", "inventory-sync"];

const VendorAgentDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = VALID_TABS.includes(searchParams.get("tab") || "")
    ? searchParams.get("tab")!
    : "predictions";

  // --- State ---
  const [predictions, setPredictions] = useState<IPredictionSummary[]>([]);
  const [stats, setStats] = useState<IDashboardStats | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(24);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<IPredictionStatus | undefined>(
    undefined
  );
  const [matchMethodFilter, setMatchMethodFilter] = useState<string | undefined>(
    undefined
  );
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [leadTimeFilter, setLeadTimeFilter] = useState<number | undefined>(
    undefined
  );

  // Lead time dropdown options — prefer stats endpoint, fall back to current page
  const leadTimeOptions = useMemo(() => {
    const raw = stats?.distinct_lead_times?.length
      ? stats.distinct_lead_times
      : predictions
          .map((p) => p.prediction.predicted_lead_time)
          .filter((v): v is number => v != null);
    const unique = Array.from(new Set(raw)).sort((a, b) => a - b);
    return unique.map((lt) => ({
      value: lt,
      label: `${lt} ${lt === 1 ? "day" : "days"}`,
    }));
  }, [stats, predictions]);

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerData, setDrawerData] = useState<IPredictionDetail | null>(null);

  // --- Filters payload (memoized) ---
  const filters: IVendorAgentDashboardFilters = useMemo(
    () => ({
      page,
      page_size: pageSize,
      search: search || undefined,
      status: statusFilter,
      match_method: matchMethodFilter,
      lead_time: leadTimeFilter,
      since: dateRange?.[0]?.startOf("day").toISOString(),
      until: dateRange?.[1]?.endOf("day").toISOString(),
    }),
    [page, pageSize, search, statusFilter, matchMethodFilter, leadTimeFilter, dateRange]
  );

  // --- Loaders ---
  const loadPredictions = useCallback(async () => {
    setLoadingList(true);
    const data = await getVendorAgentPredictions(filters);
    if (data) {
      setPredictions(data.results);
      setTotal(data.total);
    }
    setLoadingList(false);
  }, [filters]);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    const data = await getVendorAgentDashboardStats({
      since: dateRange?.[0]?.startOf("day").toISOString(),
      until: dateRange?.[1]?.endOf("day").toISOString(),
    });
    if (data) setStats(data.stats);
    setLoadingStats(false);
  }, [dateRange]);

  useEffect(() => {
    loadPredictions();
  }, [loadPredictions]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // --- Card click → open drawer + fetch detail ---
  const handleCardClick = async (predictionId: number) => {
    setDrawerOpen(true);
    setDrawerLoading(true);
    setDrawerData(null);
    const res = await getVendorAgentPredictionDetail(predictionId);
    if (res?.prediction) {
      setDrawerData(res.prediction);
    }
    setDrawerLoading(false);
  };

  const handleClearFilters = () => {
    setSearch("");
    setStatusFilter(undefined);
    setMatchMethodFilter(undefined);
    setLeadTimeFilter(undefined);
    setDateRange(null);
    setPage(1);
  };

  const handleRefresh = () => {
    loadPredictions();
    loadStats();
  };

  return (
    <div className={styles.container}>
      {/* ===== Header ===== */}
      <div className={styles.headerRow}>
        <div className={styles.headerLeft}>
          <h1 className={styles.pageTitle}>
            <RobotOutlined style={{ color: "#24aab7" }} />
            Vendor Agent Dashboard
          </h1>
          <p className={styles.pageSubtitle}>
            Audit how the AI agent analysed vendor messages and updated cart
            lead times
          </p>
        </div>
        <Tooltip title="Refresh">
          <Button
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            className={styles.refreshBtn}
            loading={loadingList || loadingStats}
          >
            Refresh
          </Button>
        </Tooltip>
      </div>

      {/* ===== Tabs ===== */}
      <Tabs
        activeKey={initialTab}
        onChange={(key) => setSearchParams({ tab: key }, { replace: true })}
        size="large"
        items={[
          {
            key: "predictions",
            label: (
              <span>
                <RobotOutlined /> Lead Time Predictions
              </span>
            ),
            children: (
              <>
                {/* ===== Stats ===== */}
                <StatsRow stats={stats} loading={loadingStats} />

                {/* ===== Filters ===== */}
                <div className={styles.filterBar}>
                  <div className={styles.filterField}>
                    <span className={styles.filterLabel}>Search</span>
                    <Input
                      allowClear
                      placeholder="Cart number, product, message…"
                      prefix={
                        <SearchOutlined style={{ color: "#94a3b8" }} />
                      }
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(1);
                      }}
                    />
                  </div>

                  <div className={styles.filterField}>
                    <span className={styles.filterLabel}>Status</span>
                    <Select
                      allowClear
                      placeholder="Any status"
                      value={statusFilter}
                      onChange={(v) => {
                        setStatusFilter(v);
                        setPage(1);
                      }}
                      options={[
                        { value: "applied", label: "Applied" },
                        { value: "would_apply", label: "Would Apply" },
                        { value: "skipped", label: "Skipped" },
                      ]}
                    />
                  </div>

                  <div className={styles.filterField}>
                    <span className={styles.filterLabel}>Match Method</span>
                    <Select
                      allowClear
                      placeholder="Any method"
                      value={matchMethodFilter}
                      onChange={(v) => {
                        setMatchMethodFilter(v);
                        setPage(1);
                      }}
                      options={[
                        { value: "exact_quote", label: "Exact Quote" },
                        { value: "quoted_chain", label: "Quoted Chain" },
                        { value: "quoted_body", label: "Quoted Body" },
                        { value: "cart_number", label: "Cart Number" },
                        { value: "md_context", label: "MD Context" },
                        { value: "recent_inquiry", label: "Recent Inquiry" },
                        { value: "cart_number_relaxed", label: "Cart Number Relaxed" },
                        { value: "single_item", label: "Single Item" },
                        { value: "multi_item", label: "Multi Item" },
                        { value: "auto_update", label: "Auto Update" },
                        { value: "numeric_code", label: "Numeric Code" },
                        { value: "no_match", label: "No Match" },
                      ]}
                    />
                  </div>

                  <div className={styles.filterField}>
                    <span className={styles.filterLabel}>Lead Time</span>
                    <Select
                      allowClear
                      placeholder="Any lead time"
                      value={leadTimeFilter}
                      onChange={(v) => {
                        setLeadTimeFilter(v);
                        setPage(1);
                      }}
                      options={leadTimeOptions}
                    />
                  </div>

                  <div className={styles.filterField}>
                    <span className={styles.filterLabel}>Date Range</span>
                    <RangePicker
                      value={dateRange as any}
                      onChange={(v) => {
                        setDateRange(v as [Dayjs, Dayjs] | null);
                        setPage(1);
                      }}
                    />
                  </div>

                  <Button
                    onClick={handleClearFilters}
                    className={styles.clearBtn}
                  >
                    Clear
                  </Button>
                </div>

                {/* ===== Cards Grid ===== */}
                {loadingList ? (
                  <div className={styles.centerWrap}>
                    <Spin size="large" />
                  </div>
                ) : predictions.length === 0 ? (
                  <div className={styles.centerWrap}>
                    <InboxOutlined className={styles.emptyIcon} />
                    <h3 className={styles.emptyTitle}>No predictions yet</h3>
                    <p className={styles.emptySubtitle}>
                      Once the agent processes vendor WhatsApp messages, their
                      analyses and updates will appear here as cards.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className={styles.cardsGrid}>
                      {predictions.map((p) => (
                        <PredictionCard
                          key={p.id}
                          data={p}
                          onClick={() => handleCardClick(p.id)}
                        />
                      ))}
                    </div>

                    <div className={styles.paginationRow}>
                      <Pagination
                        current={page}
                        total={total}
                        pageSize={pageSize}
                        onChange={(p) => setPage(p)}
                        showSizeChanger={false}
                        showTotal={(t, [from, to]) => `${from}-${to} of ${t}`}
                      />
                    </div>
                  </>
                )}
              </>
            ),
          },
          {
            key: "cart-items",
            label: (
              <span>
                <NodeIndexOutlined /> Cart Items Lifecycle
              </span>
            ),
            children: (
              <CartItemsTab onOpenPredictionDetail={handleCardClick} />
            ),
          },
          {
            key: "inventory-sync",
            label: (
              <span>
                <SyncOutlined /> Inventory Sync
              </span>
            ),
            children: <InventorySyncTab />,
          },
        ]}
      />

      {/* ===== Detail Drawer ===== */}
      <DetailDrawer
        open={drawerOpen}
        loading={drawerLoading}
        data={drawerData}
        onClose={() => setDrawerOpen(false)}
        onRemarksUpdated={(id, newRemarks) => {
          // Update local drawerData so the drawer reflects the save
          if (drawerData && drawerData.id === id) {
            setDrawerData({ ...drawerData, remarks: newRemarks });
          }
          // Update the card in the list too
          setPredictions((prev) =>
            prev.map((p) => (p.id === id ? { ...p, remarks: newRemarks } : p))
          );
        }}
      />
    </div>
  );
};

export default VendorAgentDashboard;
